import React, { useState } from 'react';
import type { AdminCustomerMutationInput, AdminCustomerSummary } from '../../hooks/useAdminApi';

interface CustomerFormProps {
  customer?: Pick<AdminCustomerSummary, 'id' | 'name' | 'email' | 'phone' | 'description' | 'stripe_customer_id'>;
  onSubmit: (input: AdminCustomerMutationInput) => Promise<void>;
  onCancel: () => void;
}

export default function CustomerForm({ customer, onSubmit, onCancel }: CustomerFormProps): React.JSX.Element {
  const [name, setName] = useState(customer?.name ?? '');
  const [email, setEmail] = useState(customer?.email ?? '');
  const [phone, setPhone] = useState(customer?.phone ?? '');
  const [description, setDescription] = useState(customer?.description ?? '');
  const [addrLine1, setAddrLine1] = useState('');
  const [addrLine2, setAddrLine2] = useState('');
  const [addrCity, setAddrCity] = useState('');
  const [addrState, setAddrState] = useState('');
  const [addrPostal, setAddrPostal] = useState('');
  const [addrCountry, setAddrCountry] = useState('');
  const [shipName, setShipName] = useState('');
  const [shipPhone, setShipPhone] = useState('');
  const [invoiceFooter, setInvoiceFooter] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showAdvanced, setShowAdvanced] = useState(false);

  async function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      const input: AdminCustomerMutationInput = { name, email };
      if (phone) input.phone = phone;
      if (description) input.description = description;
      if (addrLine1 || addrCity || addrState || addrPostal || addrCountry) {
        input.address = { line1: addrLine1, line2: addrLine2, city: addrCity, state: addrState, postal_code: addrPostal, country: addrCountry };
      }
      if (shipName || shipPhone) {
        input.shipping = { name: shipName, phone: shipPhone };
      }
      if (invoiceFooter) {
        input.invoice_settings = { footer: invoiceFooter };
      }
      await onSubmit(input);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unexpected error');
    } finally {
      setSubmitting(false);
    }
  }

  const inputClass = 'mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-cyan-400 focus:outline-none focus:ring-2 focus:ring-cyan-200';
  const labelClass = 'block text-sm font-medium text-slate-700';

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-slate-900">
          {customer ? 'Edit Customer' : 'New Customer'}
        </h2>
        <button type="button" onClick={onCancel} className="text-lg text-slate-400 hover:text-slate-600" aria-label="Close">×</button>
      </div>

      {customer?.stripe_customer_id && (
        <div className="rounded-md bg-cyan-50 px-3 py-2 text-xs text-cyan-700">
          ✓ Synced to Stripe: <code className="font-mono">{customer.stripe_customer_id}</code>
        </div>
      )}

      <div>
        <label htmlFor="customer-name" className={labelClass}>Name *</label>
        <input id="customer-name" value={name} onChange={(e) => setName(e.target.value)} className={inputClass} required />
      </div>
      <div>
        <label htmlFor="customer-email" className={labelClass}>Email *</label>
        <input id="customer-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} className={inputClass} required />
      </div>
      <div>
        <label htmlFor="customer-phone" className={labelClass}>Phone</label>
        <input id="customer-phone" value={phone} onChange={(e) => setPhone(e.target.value)} className={inputClass} placeholder="+1 555-000-0000" />
      </div>
      <div>
        <label htmlFor="customer-desc" className={labelClass}>Description</label>
        <textarea id="customer-desc" value={description} onChange={(e) => setDescription(e.target.value)} className={inputClass} rows={2} placeholder="Internal notes about this customer" />
      </div>

      <button type="button" onClick={() => setShowAdvanced(!showAdvanced)} className="text-sm text-cyan-600 hover:text-cyan-700">
        {showAdvanced ? '▾ Hide advanced fields' : '▸ Show advanced fields (address, shipping, invoice)'}
      </button>

      {showAdvanced && (
        <div className="space-y-4 rounded-lg border border-slate-200 p-4">
          <div>
            <p className="text-sm font-semibold text-slate-700">Billing Address</p>
            <div className="mt-2 grid grid-cols-2 gap-3">
              <div className="col-span-2"><label className="text-xs text-slate-500">Address Line 1</label><input value={addrLine1} onChange={(e) => setAddrLine1(e.target.value)} className={inputClass} placeholder="123 Main St" /></div>
              <div className="col-span-2"><label className="text-xs text-slate-500">Address Line 2</label><input value={addrLine2} onChange={(e) => setAddrLine2(e.target.value)} className={inputClass} placeholder="Suite 100" /></div>
              <div><label className="text-xs text-slate-500">City</label><input value={addrCity} onChange={(e) => setAddrCity(e.target.value)} className={inputClass} /></div>
              <div><label className="text-xs text-slate-500">State/Province</label><input value={addrState} onChange={(e) => setAddrState(e.target.value)} className={inputClass} /></div>
              <div><label className="text-xs text-slate-500">Postal Code</label><input value={addrPostal} onChange={(e) => setAddrPostal(e.target.value)} className={inputClass} /></div>
              <div><label className="text-xs text-slate-500">Country (2-letter)</label><input value={addrCountry} onChange={(e) => setAddrCountry(e.target.value)} className={inputClass} placeholder="US" maxLength={2} /></div>
            </div>
          </div>
          <div>
            <p className="text-sm font-semibold text-slate-700">Shipping Address</p>
            <div className="mt-2 grid grid-cols-2 gap-3">
              <div><label className="text-xs text-slate-500">Shipping Name</label><input value={shipName} onChange={(e) => setShipName(e.target.value)} className={inputClass} /></div>
              <div><label className="text-xs text-slate-500">Shipping Phone</label><input value={shipPhone} onChange={(e) => setShipPhone(e.target.value)} className={inputClass} /></div>
            </div>
          </div>
          <div>
            <p className="text-sm font-semibold text-slate-700">Invoice Settings</p>
            <div className="mt-2"><label className="text-xs text-slate-500">Invoice Footer</label><textarea value={invoiceFooter} onChange={(e) => setInvoiceFooter(e.target.value)} className={inputClass} rows={2} placeholder="Custom footer text on invoices" /></div>
          </div>
        </div>
      )}

      {error && <p className="text-sm text-red-600">{error}</p>}

      <div className="flex justify-end gap-3 pt-2">
        <button type="button" onClick={onCancel} disabled={submitting} className="rounded-md border border-slate-300 px-4 py-2 text-sm text-slate-700 hover:bg-slate-50 disabled:opacity-50">Cancel</button>
        <button type="submit" disabled={submitting} className="rounded-md bg-cyan-500 px-4 py-2 text-sm font-medium text-slate-950 hover:bg-cyan-400 disabled:opacity-50">
          {submitting ? 'Saving…' : customer ? 'Save Changes' : 'Create Customer'}
        </button>
      </div>
    </form>
  );
}